import { useEffect, useRef, useState, type ReactNode } from 'react'

// About ten lines at the table's 20px line height, plus cell padding.
const collapsedHeight = 224

export default function TableCellOverflow({ children, editing, height }: { children: ReactNode; editing: boolean; height?: number }) {
  const content = useRef<HTMLDivElement>(null)
  const viewport = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)
  const [hiddenAbove, setHiddenAbove] = useState(false)
  const [hiddenBelow, setHiddenBelow] = useState(false)
  const updateScrollEdges = (element: HTMLDivElement) => {
    setHiddenAbove(element.scrollTop > 1)
    // Scroll positions can be fractional while scrollHeight/clientHeight are rounded.
    setHiddenBelow(element.scrollHeight - element.clientHeight - element.scrollTop > 1)
  }
  useEffect(() => {
    const element = content.current
    const container = viewport.current
    if (!element || !container) return
    const measure = () => {
      setOverflows(element.getBoundingClientRect().height > (height === undefined ? collapsedHeight : height - 1))
      updateScrollEdges(container)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    observer.observe(container)
    return () => observer.disconnect()
  }, [height])
  const scrollable = overflows
  const fade = hiddenAbove
    ? hiddenBelow
      ? '[mask-image:linear-gradient(to_bottom,transparent,black_48px,black_calc(100%_-_48px),transparent)]'
      : '[mask-image:linear-gradient(to_bottom,transparent,black_48px)]'
    : hiddenBelow
    ? '[mask-image:linear-gradient(to_bottom,black_calc(100%_-_48px),transparent)]'
    : '[mask-image:none]'
  return (
    <div
      ref={viewport}
      data-table-cell-scroll
      onScroll={(event) => updateScrollEdges(event.currentTarget)}
      role={scrollable ? 'region' : undefined}
      aria-label={scrollable ? 'Scrollable cell contents' : undefined}
      tabIndex={scrollable ? 0 : undefined}
      className={
        scrollable
          ? `overflow-y-auto overscroll-y-none [scrollbar-width:thin] [scrollbar-color:transparent_transparent] hover:[scrollbar-color:theme(colors.brand-neutral.400)_transparent] focus-within:[scrollbar-color:theme(colors.brand-neutral.400)_transparent] ${fade} hover:[mask-image:none] focus-within:[mask-image:none] outline-hidden [@media(hover:none)]:[mask-image:none] [@media(hover:none)]:[scrollbar-color:theme(colors.brand-neutral.400)_transparent]`
          : 'overflow-hidden'
      }
      style={{
        height: height === undefined ? undefined : height - 1,
        maxHeight: height === undefined ? collapsedHeight : height - 1,
        maskImage: editing ? 'none' : undefined,
      }}
    >
      <div ref={content}>{children}</div>
    </div>
  )
}
